import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { 
  Truck, 
  Zap, 
  Shield, 
  Clock, 
  Star, 
  CheckCircle2,
  ArrowRight,
  MessageSquare,
  CreditCard,
  Users,
  Menu,
  X
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate(user.role === 'customer' ? '/dashboard' : '/provider');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="bg-gray-900 rounded-full p-2 shadow-lg ring-2 ring-primary/30">
                <img 
                  src="/thm-logo.png" 
                  alt="The Hotshot Marketplace" 
                  className="h-12 w-auto"
                />
              </div>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                How It Works
              </a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </a>
              <a href="#providers" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                For Providers
              </a>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <Button onClick={handleGetStarted} data-testid="dashboard-btn">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="register-btn">
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden pt-4 pb-2 space-y-4"
            >
              <a href="#how-it-works" className="block py-2 text-sm font-medium">How It Works</a>
              <a href="#pricing" className="block py-2 text-sm font-medium">Pricing</a>
              <a href="#providers" className="block py-2 text-sm font-medium">For Providers</a>
              <div className="flex flex-col gap-2 pt-4">
                {isAuthenticated ? (
                  <Button onClick={handleGetStarted} className="w-full">Go to Dashboard</Button>
                ) : (
                  <>
                    <Link to="/login" className="w-full">
                      <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white">
                        Sign In (Existing Users)
                      </Button>
                    </Link>
                    <Link to="/register" className="w-full">
                      <Button className="w-full">Get Started (New Users)</Button>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1720127601642-7c3a7ba88f5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxwaWNrdXAlMjB0cnVjayUyMGhhdWxpbmclMjB0cmFpbGVyJTIwaGlnaHdheXxlbnwwfHx8fDE3NzA0NTYwMTR8MA&ixlib=rb-4.1.0&q=85)`,
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/80 to-secondary/60" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-sm mb-6">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Hotshot-Only Platform</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              URGENT FREIGHT.
              <br />
              <span className="text-primary">READY DRIVERS.</span>
              <br />
              ZERO NOISE.
            </h1>
            
            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl">
              The Hotshot Marketplace connects your urgent loads with verified hotshot drivers—fast. 
              No general freight clutter. Just hotshot jobs that need to move now.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                className="btn-active text-base px-8"
                data-testid="hero-cta-btn"
              >
                Post a Job Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Link to="/register?role=provider">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="btn-active text-base px-8 border-white/30 text-white hover:bg-white/10"
                  data-testid="provider-signup-btn"
                >
                  I'm a Provider
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">Free to Post</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">Secure Payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-primary" />
                <span className="text-sm text-gray-300">Verified Providers</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Customers */}
      <section id="how-it-works" className="py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              HOW IT WORKS
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get your urgent freight moving in minutes, not hours
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              { 
                icon: Users, 
                title: "Create Account", 
                desc: "Quick registration—free for customers",
                step: "01"
              },
              { 
                icon: MessageSquare, 
                title: "Post Your Job", 
                desc: "Enter load details, pickup & delivery info",
                step: "02"
              },
              { 
                icon: Zap, 
                title: "Instant Alerts", 
                desc: "Matching providers notified via SMS & email",
                step: "03"
              },
              { 
                icon: CheckCircle2, 
                title: "Choose & Book", 
                desc: "Compare up to 5 quotes, book with confidence",
                step: "04"
              },
            ].map((item, i) => (
              <motion.div 
                key={i}
                variants={fadeInUp}
                className="relative p-6 bg-card border border-border rounded-sm card-hover"
              >
                <span className="absolute top-4 right-4 text-5xl font-bold text-muted/50" style={{ fontFamily: 'Barlow Condensed' }}>
                  {item.step}
                </span>
                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                PAYMENT PROTECTION
                <br />
                <span className="text-primary">FOR EVERYONE</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  { 
                    icon: CreditCard, 
                    title: "Secure Escrow", 
                    desc: "Payments held securely until job completion" 
                  },
                  { 
                    icon: Shield, 
                    title: "Dispute Protection", 
                    desc: "Funds protected during any disputes" 
                  },
                  { 
                    icon: CheckCircle2, 
                    title: "Guaranteed Release", 
                    desc: "Provider paid upon confirmed completion" 
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="aspect-square bg-secondary rounded-sm overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1740914994154-c3273e113336?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzJTIwd29ya2VyfGVufDB8fHx8MTc3MDQ1NTk5NXww&ixlib=rb-4.1.0&q=85"
                  alt="Logistics worker"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-sm p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>A$0</p>
                    <p className="text-sm text-muted-foreground">Listing Fees</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              SIMPLE PRICING
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              No hidden fees. Pay only when you succeed.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Customer Pricing */}
            <motion.div 
              className="bg-card border border-border rounded-sm overflow-hidden"
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <div className="p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 rounded-sm mb-4">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-blue-500">For Customers</span>
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  FREE TO POST
                </h3>
                <p className="text-muted-foreground mb-6">
                  Post unlimited jobs at no cost
                </p>
                <ul className="space-y-3 mb-8">
                  {['No listing fees', 'Up to 5 competitive quotes', 'Secure payment protection', 'Verified providers only'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full btn-active" 
                  onClick={() => navigate('/register')}
                  data-testid="customer-signup-btn"
                >
                  Post Your First Job
                </Button>
              </div>
            </motion.div>

            {/* Provider Pricing */}
            <motion.div 
              className="bg-card border-2 border-primary rounded-sm overflow-hidden relative"
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <div className="absolute top-0 right-0 bg-primary px-3 py-1">
                <span className="text-xs font-bold text-white">POPULAR</span>
              </div>
              <div className="p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-sm mb-4">
                  <Truck className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">For Providers</span>
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  WEEKLY ACCESS
                </h3>
                <p className="text-muted-foreground mb-6">
                  Small weekly fee + service fee on completed jobs
                </p>
                <ul className="space-y-3 mb-8">
                  {['Instant job notifications', 'Limited competition (max 5 quotes)', 'Guaranteed payment', 'Build your reputation'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full btn-active" 
                  onClick={() => navigate('/register?role=provider')}
                  data-testid="provider-pricing-btn"
                >
                  Become a Provider
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Providers Section */}
      <section id="providers" className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-sm mb-6">
              <Truck className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">For Hotshot Drivers</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              WHY DRIVERS CHOOSE THM
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hotshot-only jobs, limited competition, guaranteed payment
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              { 
                icon: Zap, 
                title: "Instant Alerts", 
                desc: "Get SMS & email notifications the moment a matching job is posted" 
              },
              { 
                icon: Users, 
                title: "Limited Quotes", 
                desc: "Only 5 providers can quote per job—less competition, better odds" 
              },
              { 
                icon: Shield, 
                title: "Payment Security", 
                desc: "Funds held in escrow. You get paid when the job is done." 
              },
            ].map((item, i) => (
              <motion.div 
                key={i}
                variants={fadeInUp}
                className="p-6 bg-white/5 border border-white/10 rounded-sm"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="text-center mt-12"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Button 
              size="lg" 
              onClick={() => navigate('/register?role=provider')}
              className="btn-active"
              data-testid="provider-final-cta"
            >
              Start Hauling Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              READY TO MOVE
              <br />
              <span className="text-primary">YOUR FREIGHT?</span>
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join The Hotshot Marketplace today. Post your first job in minutes.
            </p>
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className="btn-active px-12"
              data-testid="final-cta-btn"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="bg-gray-900 rounded-full p-2 shadow-lg ring-2 ring-primary/30">
                <img 
                  src="/thm-logo.png" 
                  alt="The Hotshot Marketplace" 
                  className="h-10 w-auto"
                />
              </div>
            </div>
            <div className="flex gap-8 text-sm text-muted-foreground">
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <a href="mailto:support@thehotshotmarketplace.com.au" className="hover:text-foreground transition-colors">Support</a>
              <a href="/admin" className="hover:text-foreground transition-colors">Admin</a>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} THM. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
